import React, { useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useAuth } from 'hooks/useAuth';
import { useResponsiveScreenSizes } from 'hooks/useResponsiveScreenSizes';

import { MenuItem, MenuItemProps } from 'components/ui/MenuItem';
import { Avatar } from 'components/ui/Avatar';

import { GrandLogoHorz } from 'components/svg/GrandLogoHorz';

import GrandPursuitLogo from 'assets/grand-pursuit-logo.png';

import { colors, Fonts, spacing, transparencies } from 'styles/theme';
import { Card, Divider, Bumper, HorizontalFlexWrapper } from 'styles/shared';
import { BottomMenuWrapper } from 'styles/menuItem';

interface NavMenuItemProps extends MenuItemProps {
    link: string;
}

interface NavMenuProps {
    menuItems: Array<NavMenuItemProps>;
    isPursuitMenu?: boolean;
}

export const NavMenu = ({ menuItems, isPursuitMenu = false }: NavMenuProps) => {
    const { signOut, userProfile, userId } = useAuth();
    const { isMobileScreenSize } = useResponsiveScreenSizes();

    const [, setSearchParams] = useSearchParams();

    const parsedRawData = useMemo(
        () => JSON.parse(JSON.stringify(userProfile?.raw_data ?? {})),
        [userProfile?.raw_data]
    );

    const onOpenProfileDetails = useCallback(() => {
        if (userProfile == null || userProfile.type == null) {
            return;
        }

        setSearchParams({
            modalContent: 'profile',
            profileId: userId,
        });
    }, [setSearchParams, userId, userProfile]);

    const onSignOut = useCallback(() => {
        signOut?.();
    }, [signOut]);

    const navMenuContent = useMemo(
        () => (
            <>
                {isMobileScreenSize ? null : (
                    <>
                        {isPursuitMenu ? (
                            <HorizontalFlexWrapper $isJustifyContentCenter>
                                <img
                                    src={GrandPursuitLogo}
                                    width="90"
                                    alt="grand-logo"
                                />
                            </HorizontalFlexWrapper>
                        ) : (
                            <GrandLogoHorz width={100} />
                        )}
                        <Divider />
                    </>
                )}

                {menuItems.map(
                    ({ text, link, SVGComponent, imageUrl, isSelected }) => (
                        <Link key={link} to={link}>
                            <MenuItem
                                SVGComponent={SVGComponent}
                                imageUrl={imageUrl}
                                text={text}
                                isSelected={isSelected}
                            />
                        </Link>
                    )
                )}

                {isMobileScreenSize ? null : (
                    <>
                        <Divider />
                        <HorizontalFlexWrapper
                            $isJustifyContentCenter={
                                isPursuitMenu ? true : false
                            }
                        >
                            <button
                                onClick={onOpenProfileDetails}
                                style={{ padding: 0 }}
                            >
                                <Avatar
                                    fullName={userProfile?.full_name ?? ''}
                                    imageUrl={parsedRawData?.profile_image_url}
                                />
                            </button>
                            <Bumper $minWidth={spacing.xSmall} />
                            <button onClick={onSignOut}>
                                <Fonts.Small
                                    $color={colors.grey40}
                                    $isUnderlined
                                >{`Log Out`}</Fonts.Small>
                            </button>
                        </HorizontalFlexWrapper>
                    </>
                )}
            </>
        ),
        [
            isMobileScreenSize,
            isPursuitMenu,
            menuItems,
            onOpenProfileDetails,
            onSignOut,
            parsedRawData?.profile_image_url,
            userProfile?.full_name,
        ]
    );

    return isMobileScreenSize ? (
        <BottomMenuWrapper>
            <HorizontalFlexWrapper $isContentSpaced>
                {navMenuContent}
            </HorizontalFlexWrapper>
        </BottomMenuWrapper>
    ) : (
        <div>
            <Bumper $minHeight={spacing.xxxLarge} />
            <Bumper $minHeight={spacing.xxxLarge} />
            <Card
                $width={isPursuitMenu ? '260px' : '236px'}
                $backgroundColor={
                    isPursuitMenu ? transparencies.white95 : colors.white
                }
                $margin={`0 ${spacing.xxxLarge} 0 0`}
                $padding={spacing.large}
            >
                {navMenuContent}
            </Card>
        </div>
    );
};
